/**
 * Month and weekday abbreviations for the events date badge, one entry per language the app ships.
 *
 * WHY THIS IS BUNDLED RATHER THAN LEFT TO `Intl.DateTimeFormat`. Two failures showed up the moment
 * the formatters were exercised against real locales in a real browser:
 *
 *  1. Chrome does not carry CLDR data for every language this app supports. On the Chrome used to
 *     test this, `Intl.DateTimeFormat('mk')` and `('sq')` both resolve to `en-GB` — so Macedonian
 *     and Albanian guests, Macedonian being the home market, would silently read English month
 *     names. Node's full-ICU build has both, which is exactly why this is easy to miss locally.
 *  2. CLDR's abbreviated month for Bulgarian is the zero-padded NUMBER — `Intl` returns "04" for
 *     April, in the standalone and in-context forms alike. Spec-correct, and unreadable stacked over
 *     a day number in a date chip.
 *
 * The values below are CLDR's, extracted from a full-ICU Node build so they are the same strings a
 * complete browser would produce — with Bulgarian's months replaced by the conventional
 * abbreviations. Bundling them also makes the output identical in every browser, which a formatter
 * that silently degrades to English cannot promise.
 *
 * Weekdays are indexed by `Date.getDay()`: 0 = Sunday.
 *
 * GENERATED, then hand-corrected for `bg`. Adding a language means adding an entry here as well as
 * a locale JSON file; `dateNamesFor` falls back to English rather than throwing if that is missed.
 */

export interface EventDateNames {
  /** January..December. */
  months: string[];
  /** Sunday..Saturday, matching `Date.getDay()`. */
  weekdays: string[];
}

export const EVENT_DATE_NAMES: { [lang: string]: EventDateNames } = {
  en: {
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  },
  mk: {
    months: ["јан.", "фев.", "мар.", "апр.", "мај", "јун.", "јул.", "авг.", "сеп.", "окт.", "ное.", "дек."],
    weekdays: ["нед.", "пон.", "вто.", "сре.", "чет.", "пет.", "саб."]
  },
  sq: {
    months: ["jan", "shk", "mar", "pri", "maj", "qer", "korr", "gush", "sht", "tet", "nën", "dhj"],
    weekdays: ["die", "hën", "mar", "mër", "enj", "pre", "sht"]
  },
  sr: {
    months: ["јан", "феб", "мар", "апр", "мај", "јун", "јул", "авг", "сеп", "окт", "нов", "дец"],
    weekdays: ["нед", "пон", "уто", "сре", "чет", "пет", "суб"]
  },
  hr: {
    months: ["sij", "velj", "ožu", "tra", "svi", "lip", "srp", "kol", "ruj", "lis", "stu", "pro"],
    weekdays: ["ned", "pon", "uto", "sri", "čet", "pet", "sub"]
  },
  bg: {
    months: ["яну", "фев", "мар", "апр", "май", "юни", "юли", "авг", "сеп", "окт", "ное", "дек"],
    weekdays: ["нд", "пн", "вт", "ср", "чт", "пт", "сб"]
  },
  el: {
    months: ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μαΐ", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"],
    weekdays: ["Κυρ", "Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ"]
  },
  de: {
    months: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
    weekdays: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]
  },
  fr: {
    months: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    weekdays: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."]
  },
  it: {
    months: ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"],
    weekdays: ["dom", "lun", "mar", "mer", "gio", "ven", "sab"]
  },
  nl: {
    months: ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"],
    weekdays: ["zo", "ma", "di", "wo", "do", "vr", "za"]
  },
  pl: {
    months: ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"],
    weekdays: ["niedz.", "pon.", "wt.", "śr.", "czw.", "pt.", "sob."]
  },
  ro: {
    months: ["ian.", "feb.", "mar.", "apr.", "mai", "iun.", "iul.", "aug.", "sept.", "oct.", "nov.", "dec."],
    weekdays: ["dum.", "lun.", "mar.", "mie.", "joi", "vin.", "sâm."]
  },
  sv: {
    months: ["jan.", "feb.", "mars", "apr.", "maj", "juni", "juli", "aug.", "sep.", "okt.", "nov.", "dec."],
    weekdays: ["sön", "mån", "tis", "ons", "tors", "fre", "lör"]
  },
  tr: {
    months: ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"],
    weekdays: ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"]
  }
};

/** The table for a language, falling back to English for anything not bundled. */
export function dateNamesFor(lang: string): EventDateNames {
  return EVENT_DATE_NAMES[lang] || EVENT_DATE_NAMES['en'];
}
